import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../context/ToastContext";
import { api } from "../lib/api";
import { autoPrintReceipt, type ReceiptIdentity } from "../lib/hardware";
import type { Company, Locale } from "../lib/types";
import { keys } from "./queries";
import { useLocale } from "./useLocale";

/** The receipt header/footer for a language, from the Company details the manager entered. */
export function receiptIdentity(c: Company, locale: Locale, labels: { phone: string; taxNo: string }): ReceiptIdentity {
  const ar = locale === "ar";
  return {
    stationName: (ar ? c.stationNameAr : c.stationName) || c.stationName,
    headerLines: [ar ? c.addressAr || c.address : c.address, c.phone && `${labels.phone}: ${c.phone}`, c.taxNumber && `${labels.taxNo}: ${c.taxNumber}`].filter(Boolean) as string[],
    footer: (ar ? c.receiptFooterAr : c.receiptFooter) || null,
  };
}

/**
 * Prints a receipt automatically after a sale (native thermal printer only). A failure must be
 * visible: the sale is already recorded, but the cashier needs to know the paper did not come out.
 */
export function useAutoPrint() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast } = useToast();
  const qc = useQueryClient();
  return useCallback(
    (transactionId: string, barcode?: string) => {
      void (async () => {
        try {
          const { company } = await qc.fetchQuery({ queryKey: keys.company, queryFn: () => api.get<{ company: Company }>("/settings/company"), staleTime: 60_000 });
          const labels = { phone: t("company.phone"), taxNo: t("company.taxNo") };
          await autoPrintReceipt(transactionId, locale, (receiptLocale) => receiptIdentity(company, receiptLocale, labels), barcode);
        } catch (err) {
          toast(t("errors.PRINT_FAILED", { reason: err instanceof Error ? err.message : String(err) }), "error");
        }
      })();
    },
    [locale, qc, t, toast],
  );
}
